import React, { useEffect, useRef, useState, useCallback } from 'react'
import styled, { css } from 'styled-components'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { down } from 'styled-breakpoints'
import { useBreakpoint } from 'styled-breakpoints/react-styled'

import { ReactComponent as Circle } from '../../assets/svg/circle.svg'
import iphone from '../../assets/images/iphone-mockup.png'
import screen1 from '../../assets/images/screen-1.png'
import screen2 from '../../assets/images/screen-2.png'
import screen3 from '../../assets/images/screen-3.png'
import { TABLET } from '../../lib/constant'

const HEIGHT = 4500

export default function ScrollSection() {
	const [index, setIndex] = useState(1)
	const [stickyTop, setStickyTop] = useState(0)
	const isTablet = useBreakpoint(down(TABLET))

	const indexes = {
		screen1: () => setIndex(1),
		screen2: () => setIndex(2),
		screen3: () => setIndex(3),
	}

	const handleIntersection = useCallback((entries) => {
		entries.forEach((entry) => {
			if (entry.intersectionRatio > 0) {
				const id = entry.target.getAttribute('id')
				indexes[id]()
			}
		})
	}, [])

	const documentRef = useRef(null)
	useEffect(() => {
		documentRef.current = document
	}, [])

	useEffect(() => {
		if (documentRef.current) {
			const windowHeight = window.innerHeight
			if (isTablet) {
				setStickyTop(16)
				return
			}
			if (windowHeight > 1200) {
				setStickyTop((windowHeight - 890) / 2 - 72)
			} else {
				setStickyTop(72)
			}
		}
	}, [documentRef, isTablet])

	useEffect(() => {
		const observerConfig = {
			rootMargin: `30% 0px -50% 0px`,
			thredhold: 0,
		}

		const observer = new IntersectionObserver(
			handleIntersection,
			observerConfig
		)

		const observerSections = document.querySelectorAll('.observer-section')
		observerSections.forEach((section) => {
			observer.observe(section)
		})

		return () => observer.disconnect()
	}, [handleIntersection])

	const containerRef = useRef(null)
	const screensRef = useRef(null)
	useEffect(() => {
		if (!containerRef.current || !screensRef.current) {
			return null
		}
		const screens = screensRef.current

		const windowWidth = window.innerWidth
		const isMobile = windowWidth < 1023
		const secondY = isMobile ? 507 : 779
		const thirdY = isMobile ? 1014 : 1559

		const tl = gsap
			.timeline({
				scrollTrigger: {
					id: 'card',
					trigger: containerRef.current,
					scrub: 0.2,
					start: `top top`,
					end: `+=${HEIGHT / 2}`,
					refreshPriority: -1,
					invalidateOnRefresh: true,
				},
			})
			.to(screens, {
				y: 0,
			})
			.set(
				screens,
				{
					y: secondY * -1,
				},
				'+=0.5'
			)
			.set(
				screens,
				{
					y: thirdY * -1,
				},
				'+=1'
			)

		ScrollTrigger.matchMedia({
			// DESKTOP_L
			'(min-width: 1440px)': () => {
				ScrollTrigger.refresh()
			},
			// DESKTOP_S
			'(max-width: 1439px) and (min-width: 1024px)': () => {
				ScrollTrigger.refresh()
			},
			// TABLET
			'(max-width: 1023px) and (min-width: 768px)': () => {
				ScrollTrigger.refresh()
			},
			// MOBILE
			'(max-width: 767px)': () => {
				ScrollTrigger.refresh()
			},
		})

		return () => tl.kill()
	}, [isTablet])

	return (
		<Section ref={containerRef}>
			<Wrapper>
				<StickyBlock top={stickyTop}>
					<p>타고가 이용 방법</p>
					<h1>
						<Em selected={index === 1}>위치찾고, </Em>
						<Em selected={index === 2}>스캔하고, </Em>
						<Em selected={index === 3}>즐기기</Em>
					</h1>
					<Content className="content">
						{isTablet ? (
							<MobileStepper>
								<MobileStepperItem selected={index === 1}>
									<CircleIcon />
									<p>위치찾기</p>
								</MobileStepperItem>
								<span className="divider">-</span>
								<MobileStepperItem selected={index === 2}>
									<CircleIcon />
									<p>스캔하기</p>
								</MobileStepperItem>
								<span className="divider">-</span>
								<MobileStepperItem selected={index === 3}>
									<CircleIcon />
									<p>즐기기</p>
								</MobileStepperItem>
							</MobileStepper>
						) : (
							<Stepper className="stepper">
								<StepperItem selected={index === 1}>
									<CircleIcon />
									<p>위치찾기</p>
								</StepperItem>
								<StepperItem selected={index === 2}>
									<CircleIcon />
									<p>스캔하기</p>
								</StepperItem>
								<StepperItem selected={index === 3}>
									<CircleIcon />
									<p>즐기기</p>
								</StepperItem>
							</Stepper>
						)}

						<Device>
							<img src={iphone} alt="screen" className="iphone" />
							<Screens className="screens" ref={screensRef}>
								<Screen
									className="observer-section"
									id="screen1"
									selected={index === 1}
								>
									<img src={screen1} alt="screen" />
								</Screen>
								<Screen
									className="observer-section"
									id="screen2"
									selected={index === 2}
								>
									<img src={screen2} alt="screen" />
								</Screen>
								<Screen
									className="observer-section"
									id="screen3"
									selected={index === 3}
								>
									<img src={screen3} alt="screen" />
								</Screen>
							</Screens>
						</Device>
						<Descriptions>
							<Description selected={index === 1}>
								현재 위치에서 가까운 <br /> 타고가를 찾아주세요.
							</Description>
							<Description selected={index === 2}>
								타고가 상단에 있는 <br /> QR코드를 스캔해주세요.
							</Description>
							<Description selected={index === 3}>
								이제 모든 과정이 끝! <br /> 안전하게 타고가를 즐기세요!
							</Description>
						</Descriptions>
					</Content>
				</StickyBlock>
			</Wrapper>
		</Section>
	)
}

const Section = styled.section`
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 0 100px;
	box-sizing: border-box;
	margin-top: 196px;
`

const Wrapper = styled.div`
	height: ${HEIGHT}px;
	width: 100vw;
	padding: 0 16px;
	max-width: 1180px;
	padding: 0 100px;

	${down(TABLET)} {
		padding: 0 16px;
	}
`

const StickyBlock = styled.div`
	position: sticky;
	position: -webkit-sticky;
	top: ${({ top }) => top}px;
	width: 100%;
	text-align: center;
	font-weight: 700;

	> p {
		color: #d84576;
		margin-bottom: 16px;
		font-size: 16px;
		line-height: 24px;
	}

	> h1 {
		font-size: 80px;
		line-height: 88px;
	}

	${down(TABLET)} {
		> h1 {
			font-size: 48px;
			line-height: 56px;
		}
	}
`

const Em = styled.em`
	font-style: normal;
	color: ${({ selected }) => selected && '#ff6d60'};
	word-break: keep-all;
`

const Content = styled.div`
	display: flex;
	justify-content: space-between;
	margin-top: 32px;
	width: 100%;

	${down(TABLET)} {
		flex-direction: column;
		align-items: center;
	}
`

const Stepper = styled.ul`
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: center;
	position: sticky;
	top: 196px;
`

const CircleIcon = styled(Circle)``

const MobileStepper = styled.ul`
	display: flex;
	margin-bottom: 32px;
	align-items: center;
	.divider {
		margin: 0 12px;
		color: #ccc;
	}
`

const MobileStepperItem = styled.li`
	font-size: 16px;
	line-height: 24px;
	font-weight: 700;
	display: flex;
	align-items: center;
	position: relative;
	color: #ccc;
	transition: all 0.5s;

	${({ selected }) =>
		selected &&
		css`
			color: #ff6d60;

			${CircleIcon} {
				transition: all 0.5s;
				fill: #ff6d60;
			}
		`}

	& + & {
		margin-left: 24px;
	}

	& > svg {
		fill: #ccc;
		width: 16px;
		height: 16px;
		margin-right: 8px;
	}

	& > p {
		display: block;
	}
`

const StepperItem = styled.li`
	font-size: 32px;
	line-height: 40px;
	font-weight: 700;
	display: flex;
	align-items: center;
	position: relative;
	color: #ccc;
	transition: all 0.5s;

	${({ selected }) =>
		selected &&
		css`
			color: #ff6d60;

			${CircleIcon} {
				transition: all 0.5s;
				fill: #ff6d60;
			}
		`}

	& + & {
		margin-top: 56px;
	}

	& > svg {
		fill: #ccc;
		width: 16px;
		height: 16px;
		margin-right: 32px;
	}

	& > p {
		display: block;
	}

	&:not(:last-child) {
		&::after {
			content: '';
			display: block;
			position: absolute;
			width: 1px;
			height: 56px;
			top: 40px;
			left: 7px;
			background-color: #ccc;
		}
	}
`

const Device = styled.div`
	width: 380px;
	height: 770px;
	border-radius: 32px;
	position: relative;
	overflow: hidden;

	img {
		&.iphone {
			width: 100%;
		}
	}

	${down(TABLET)} {
		width: 240px;
		height: 486px;
	}
`

const Screens = styled.div`
	position: absolute;
	width: calc(100% - 40px);
	top: 16px;
	left: 50%;
	transform: translateX(-50%);

	&::-webkit-scrollbar {
		display: none !important;
	}

	& > *:not(:last-child) {
		margin-bottom: 40px;
	}

	${down(TABLET)} {
		top: 10px;
		width: calc(100% - 20px);
	}
`

const Screen = styled.div`
	img {
		width: 100%;
	}

	${down(TABLET)} {
		img {
			width: 215px;
			height: 464px;
		}
	}
`

const Descriptions = styled.div`
	flex: 1;
	display: flex;
	align-items: center;
	box-sizing: border-box;
	z-index: 99;
`

const Description = styled.div`
	display: ${({ selected }) => (selected ? 'block' : 'none')};
	margin-left: auto;
	font-size: 18px;
	line-height: 32px;
	transition: all 0.5s;

	${down(TABLET)} {
		margin-left: 0;
		margin-top: 32px;
		max-width: 240px;
		font-weight: 400;
	}
`
